import { Mail, Phone, MapPin, Calendar } from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import type { Customer } from "./types";

interface CustomerInfoPanelProps {
  customer: Customer;
  isEditing: boolean;
  editData: Partial<Customer>;
  onEditDataChange: (data: Partial<Customer>) => void;
}

function CustomerEditForm({
  editData,
  onEditDataChange,
}: {
  editData: Partial<Customer>;
  onEditDataChange: (data: Partial<Customer>) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            First name
          </label>
          <input
            type="text"
            value={editData.firstName || ""}
            onChange={(e) => onEditDataChange({ ...editData, firstName: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Last name
          </label>
          <input
            type="text"
            value={editData.lastName || ""}
            onChange={(e) => onEditDataChange({ ...editData, lastName: e.target.value })}
            className="input"
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Email
        </label>
        <input
          type="email"
          value={editData.email || ""}
          onChange={(e) => onEditDataChange({ ...editData, email: e.target.value })}
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Phone
        </label>
        <input
          type="tel"
          value={editData.phone || ""}
          onChange={(e) => onEditDataChange({ ...editData, phone: e.target.value })}
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Street
        </label>
        <input
          type="text"
          value={editData.street || ""}
          onChange={(e) => onEditDataChange({ ...editData, street: e.target.value })}
          className="input"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            City
          </label>
          <input
            type="text"
            value={editData.city || ""}
            onChange={(e) => onEditDataChange({ ...editData, city: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Postal code
          </label>
          <input
            type="text"
            value={editData.zip || ""}
            onChange={(e) => onEditDataChange({ ...editData, zip: e.target.value })}
            className="input"
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Date of birth
        </label>
        <input
          type="date"
          value={editData.birthDate || ""}
          onChange={(e) => onEditDataChange({ ...editData, birthDate: e.target.value })}
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Passport number
        </label>
        <input
          type="text"
          value={editData.passportNumber || ""}
          onChange={(e) => onEditDataChange({ ...editData, passportNumber: e.target.value })}
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Passport expiry
        </label>
        <input
          type="date"
          value={editData.passportExpiry || ""}
          onChange={(e) => onEditDataChange({ ...editData, passportExpiry: e.target.value })}
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Notes
        </label>
        <textarea
          rows={3}
          value={editData.notes || ""}
          onChange={(e) => onEditDataChange({ ...editData, notes: e.target.value })}
          className="input"
        />
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={editData.newsletterSubscribed || false}
          onChange={(e) =>
            onEditDataChange({ ...editData, newsletterSubscribed: e.target.checked })
          }
          className="text-primary-600 h-4 w-4 rounded"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">Newsletter subscription</span>
      </label>
    </div>
  );
}

function CustomerDisplayView({ customer }: { customer: Customer }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
        <Mail className="h-5 w-5" />
        <span>{customer.email}</span>
      </div>
      {customer.phone && (
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
          <Phone className="h-5 w-5" />
          <span>{customer.phone}</span>
        </div>
      )}
      {(customer.street || customer.city) && (
        <div className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
          <MapPin className="h-5 w-5 shrink-0" />
          <div>
            {customer.street && <p>{customer.street}</p>}
            <p>
              {customer.zip} {customer.city}
              {customer.country && `, ${customer.country}`}
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
        <Calendar className="h-5 w-5" />
        <span>Registered: {formatDate(customer.createdAt)}</span>
      </div>

      {customer.birthDate && (
        <div className="border-t pt-4 dark:border-gray-700">
          <p className="text-sm text-gray-500">Date of birth</p>
          <p className="text-gray-900 dark:text-white">{formatDate(customer.birthDate)}</p>
        </div>
      )}

      {customer.passportNumber && (
        <div>
          <p className="text-sm text-gray-500">Passport number</p>
          <p className="text-gray-900 dark:text-white">
            {customer.passportNumber}
            {customer.passportExpiry && (
              <span className="ml-2 text-sm text-gray-500">
                (valid until {formatDate(customer.passportExpiry)})
              </span>
            )}
          </p>
        </div>
      )}

      {customer.notes && (
        <div className="border-t pt-4 dark:border-gray-700">
          <p className="text-sm text-gray-500">Notes</p>
          <p className="whitespace-pre-wrap text-gray-900 dark:text-white">{customer.notes}</p>
        </div>
      )}

      <div className="border-t pt-4 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {customer.newsletterSubscribed ? (
            <span className="badge bg-success-100 text-success-700">Newsletter active</span>
          ) : (
            <span className="badge bg-gray-100 text-gray-600">Newsletter inactive</span>
          )}
          {customer.source && (
            <span className="badge bg-blue-100 text-blue-700">Source: {customer.source}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CustomerInfoPanel({
  customer,
  isEditing,
  editData,
  onEditDataChange,
}: CustomerInfoPanelProps) {
  return (
    <div className="lg:col-span-1">
      <div className="card space-y-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Customer information
        </h2>

        {isEditing ? (
          <CustomerEditForm editData={editData} onEditDataChange={onEditDataChange} />
        ) : (
          <CustomerDisplayView customer={customer} />
        )}
      </div>
    </div>
  );
}
